import { resolveServiceCategoryNameFromLabel } from "./templateToServiceCategory";

/**
 * The Diligent service category names in the product catalog, as the Airtable VQQ
 * Templates "Service Categories" field lists them (every template not retired),
 * followed by legacy names that no current category replaced. A name the catalog
 * renamed or retired is not listed: templateToServiceCategory maps it to the
 * category that replaced it, so it displays under the current name.
 *
 * Names here must match the Airtable VQQ Templates "Service Categories" value
 * exactly. Airtable renders a "New" tag next to recently added rows, and a
 * previous paste of this list glued that tag onto the name, which then rendered
 * on badges verbatim. Do not paste from the Airtable grid view: copy the field
 * value, or take the name from the catalog API response.
 */
export const RFI_CATALOG_SERVICE_NAMES = new Set([
  "Foundation - GCP",
  "Foundation - GCP Business",
  "Foundation - GCP Quality",
  "Foundation - GCP Technology",
  "Foundation - General",
  "Foundation - GLP",
  "Foundation - GMP",
  "CRO Oversight",
  "Data Management",
  "Decentralized Trials",
  "Feasibility & Site Selection",
  "Investigator Site Budgets",
  "Medical Writing",
  "Project Management",
  "Site Monitoring - CRA",
  "Statistical Programming & Biostatistics",
  "Clinical IMP Management",
  "Clinical Supply Logistics",
  "Comparator Sourcing",
  "Compounding",
  "Controlled Materials",
  "Distribution & Warehouse Services",
  "DS/DP Manufacturing",
  "General Clinical IMP",
  "Packaging & Labeling",
  "Radiopharmaceutical",
  "Temperature Controlled Couriers",
  "Testing & Release (incl. QP)",
  "Bioanalytical",
  "Biomarker",
  "Central Lab",
  "ECG",
  "EEG",
  "General Analytical (Release & Stability)",
  "Imaging",
  "Microbiology",
  "Sample Logistics & Biobanking",
  "Specialty (incl. DMPK)",
  "Animal Test Systems (Vivarium) - GLP",
  "Bioanalytical - GLP",
  "Computerised Systems - GLP",
  "Documentation & Archiving - GLP",
  "Environmental Monitoring & Calibration - GLP",
  "Histopathology Laboratory - GLP",
  "Molecular Biology - GLP",
  "Specialist In-Vitro Safety Studies - GLP",
  "Test Article Formulation Analysis - GLP",
  "Test Systems & Critical Reagents - GLP",
  "Toxicology - GLP",
  "Virology - GLP",
  "HA Liaison Services",
  "IND/CTA Submissions",
  "Regulatory Affairs",
  "Medical Monitoring",
  "Medical Review & PA Qualification",
  "Pharmacovigilance",
  "Real World Data/Evidence",
  "Safety Boards & Committees",
  "Scientific Advisory",
  "Signal Detection",
  "Investigator Site",
  "Investigator Site File",
  "IRB/IEC",
  "Patient Contact Center",
  "Patient Identification & Pre-Consent Support",
  "Patient Remote Visits",
  "Recruitment, Screening & Retention",
  "Site Management - Site Networks",
  "Telemedicine",
  "Translation Services",
  "CTMS",
  "Data Hosting",
  "Data Protection & Privacy",
  "Digital Advertising",
  "eCOA/ePRO",
  "Electronic Data Capture",
  "Informed Consent",
  "Retention & Archival",
  "RTSM",
  "Trial Master File",
  "Wearables (All Types)",
  // Legacy names with no replacement in the current catalog.
  "Investigational & Medicinal Product Management",
  "QP Release Services (EU)",
  "Radiolabeled Materials",
  "eHealth Records for Patient Recruitment & Feasibility",
  "eHealth Records to EDC Connector Apps",
]);

/** Match audit/VQA styling when no catalog color is found */
export const DEFAULT_RFI_BADGE_STYLE = {
  bgColor: "#EEF2FF",
  textColor: "#4338CA",
  borderColor: "#4338CA",
};

export const DEFAULT_GRAY_RFI_BADGE_STYLE = {
  bgColor: "#F3F4F6",
  textColor: "#6B7280",
  borderColor: "#9CA3AF",
};

function normalizeForCatalogLookup(name) {
  if (!name || typeof name !== "string") return "";
  const trimmed = name.trim();
  if (!trimmed) return "";
  return resolveServiceCategoryNameFromLabel(trimmed) || trimmed;
}

/**
 * Catalog lookup, tolerant of a value that still carries Airtable's glued "New"
 * tag. Stripping is applied to the incoming value only: the returned name always
 * comes from the catalog set above, so a contaminated value can never reach a
 * badge. Never match by gluing "New" onto the candidate, that only reintroduces
 * the tag as a display name.
 */
function findCatalogName(candidate) {
  const n = normalizeForCatalogLookup(candidate);
  if (!n) return null;

  for (const variant of [n, n.replace(/New$/i, "").trim()]) {
    if (!variant) continue;
    if (RFI_CATALOG_SERVICE_NAMES.has(variant)) return variant;
    const lower = variant.toLowerCase();
    for (const k of RFI_CATALOG_SERVICE_NAMES) {
      if (k.toLowerCase() === lower) return k;
    }
  }

  return null;
}

/**
 * True if the string matches the catalog (exact, case-insensitive, or API variant without glued "New").
 */
export function isKnownRfiCatalogServiceName(rawName) {
  return findCatalogName(rawName) !== null;
}

/**
 * Badge label for RFI scope: the catalog name when the value matches the official list,
 * otherwise the custom template's own name. Collapsing customs to a literal "Custom" hid
 * which template a VQQ was for; the Type column already distinguishes Diligent from Custom.
 * Blank input falls back to "Custom". A renamed or retired category ("Foundation - All",
 * "Core Capabilities", "Recruitment & Retention") shows as the category that replaced it,
 * through templateToServiceCategory.
 */
export function getRfiCapabilityBadgeDisplayName(rawName) {
  const n = normalizeForCatalogLookup(rawName);
  if (!n) return "Custom";
  // The raw name, not the lookup candidate: normalization strips matching-only tokens
  // (Diligent prefix, RFI/VQQ suffixes) that belong in a custom name's display.
  return findCatalogName(n) ?? String(rawName).trim();
}
